import { type ReactNode } from "react";
import { LayoutGrid } from "lucide-react";
import {
  categoryCoversOf,
  categoryTileClass,
  masterLogoOf,
  type CategoryCovers,
} from "@/lib/homeCatalog";
import { categoryIcon } from "@/components/home/categoryIcons";
import { CategoryThumb } from "@/components/home/CategoryThumb";

/**
 * Baris tile kategori scroll horizontal ala Shopee. Tap = pilih kategori.
 *
 * Dipakai DUA mode:
 * - BERANDA (navigasi): `highlightActive={false}`, tanpa drill-down — tile cuma
 *   peluncur ke halaman `/kategori` (kategori tak lagi memfilter feed beranda).
 * - (Legacy) FILTER: `active`/`onSelect` toggle + drill-down segmen.
 *
 * GAMBAR TILE (`CategoryThumb`): tumpukan 2 foto sampul produk TERBARU di
 * kategori itu (`covers`, dihitung server). Ikon yang DIUNGGAH admin
 * (`product_categories.icon_url`, mig 0951) tetap menang sebagai override
 * manual; kategori tanpa foto maupun unggahan jatuh ke ikon lucide turunan-nama
 * + latar pastel (`categoryIcon`/`categoryTileClass`).
 *
 * DRILL-DOWN SEGMEN (mig 159): saat sebuah kategori terpilih dan kategori itu
 * punya segmen (mis. Gamis → Anak/Dewasa), muncul baris kedua chip segmen
 * ("Gamis › [Semua][Anak][Dewasa]"). Kategori tanpa segmen tidak memunculkan
 * baris kedua. Segmen di-reset di pemanggil setiap kategori berganti.
 */
export function CategoryRow({
  categories,
  active,
  onSelect,
  segments = [],
  activeSegment = null,
  onSelectSegment,
  highlightActive = true,
  trailing,
  logos,
  covers
}: {
  categories: string[];
  active: string | null;
  onSelect: (name: string | null) => void;
  /** Segmen yang tersedia di dalam kategori aktif (kosong → tanpa drill-down). */
  segments?: string[];
  activeSegment?: string | null;
  onSelectSegment?: (name: string | null) => void;
  /** Sorot tile aktif (mode filter). Mode navigasi beranda → false. */
  highlightActive?: boolean;
  /** Elemen kanan di judul (mis. tautan "Lihat Semua"). */
  trailing?: ReactNode;
  /**
   * Peta nama kategori → ikon unggahan dari `masterLogoMap` (key ternormalisasi).
   * Opsional: pemanggil yang belum punya master kategori tetap dapat ikon lucide.
   */
  logos?: ReadonlyMap<string, string>;
  /**
   * Foto tile per kategori dari `categoryCoverPhotos` (key ternormalisasi).
   * Opsional: tanpa foto, tile jatuh ke ikon unggahan / ikon lucide.
   */
  covers?: CategoryCovers;
}) {
  if (!categories.length) return null;

  const showSegments = Boolean(active) && segments.length > 0 && onSelectSegment;

  return (
    <section className="mt-2 bg-white py-3">
      <div className="mb-2 flex items-center justify-between px-3">
        <h2 className="text-sm font-extrabold text-ink">Kategori</h2>
        {trailing}
      </div>
      <div className="no-scrollbar flex gap-0.5 overflow-x-auto px-1.5">
        <CategoryTile
          label="Semua"
          isActive={highlightActive && active === null}
          onClick={() => onSelect(null)}
        >
          <CategoryThumb
            photos={[]}
            icon={LayoutGrid}
            colorClass="bg-shopeeSoft text-shopee"
            active={highlightActive && active === null}
            stacked
          />
        </CategoryTile>
        {categories.map((name) => (
          <CategoryTile
            key={name}
            label={name}
            isActive={highlightActive && active === name}
            onClick={() => onSelect(name)}
          >
            <CategoryThumb
              photos={categoryCoversOf(covers, name)}
              iconUrl={masterLogoOf(logos, name)}
              icon={categoryIcon(name)}
              colorClass={categoryTileClass(name)}
              active={highlightActive && active === name}
            />
          </CategoryTile>
        ))}
      </div>

      {showSegments ? (
        <div className="mt-2 flex items-center gap-1.5 overflow-x-auto px-3 no-scrollbar">
          <span className="shrink-0 text-[11px] font-bold text-muted">
            {active} ›
          </span>
          <SegmentChip
            label="Semua"
            isActive={activeSegment === null}
            onClick={() => onSelectSegment!(null)}
          />
          {segments.map((name) => (
            <SegmentChip
              key={name}
              label={name}
              isActive={activeSegment === name}
              onClick={() => onSelectSegment!(name)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function SegmentChip({
  label,
  isActive,
  onClick
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold transition ${
        isActive
          ? "bg-shopee text-white"
          : "bg-shopeeSoft text-shopee"
      }`}
    >
      {label}
    </button>
  );
}

function CategoryTile({
  label,
  isActive,
  onClick,
  children
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  /** Gambar tile (`CategoryThumb`). */
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-[5.5rem] shrink-0 flex-col items-center gap-1.5 px-0.5 py-1"
    >
      {children}
      <span
        className={`line-clamp-2 min-h-7 w-full text-center text-[11px] leading-tight ${
          isActive ? "font-bold text-shopee" : "font-semibold text-ink"
        }`}
      >
        {label}
      </span>
    </button>
  );
}
